import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { Plus, Search, Upload, Download, Trash2, MoreVertical, Pencil } from "lucide-react";
import { ChevronDownIcon } from "../../../helper/HwIcon";
import { adminApi } from "../api/adminApi";
import { getAdminToken } from "../../../helper/adminAuthStorage";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}><ChevronDownIcon /></components.DropdownIndicator>
);
const rsComponents = { DropdownIndicator, IndicatorSeparator: () => null };
const selectStyles = {
  control: (b, s) => ({
    ...b,
    minHeight: 44,
    borderRadius: 10,
    paddingLeft: 4,
    fontSize: 14,
    backgroundColor: "#f8fafc",
    borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.12)" : "none",
    ":hover": { borderColor: "#c7cfdd" },
  }),
  singleValue: (b) => ({ ...b, color: "#1b2439", fontWeight: 600 }),
  menu: (b) => ({ ...b, borderRadius: 10, overflow: "hidden", boxShadow: "0 10px 30px rgba(20,28,48,.14)" }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({
    ...b,
    fontSize: 14,
    color: s.isSelected ? "#fff" : "#1b2439",
    backgroundColor: s.isSelected ? "#3264FF" : s.isFocused ? "#eef2ff" : "#fff",
    cursor: "pointer",
  }),
};

const STATUS_OPTS = [
  { value: "", label: "Status" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "hidden", label: "Hidden" },
];

const SAMPLE_CSV = `name,propertyType,starRating,currency,description,address,cityName,countryName,postalCode,lat,lng,checkInTime,checkOutTime,frontDeskHours,phone,email,website,languages,amenities,policy_checkIn,policy_checkOut,policy_children,policy_extraBedsAndCribs,policy_deposit,policy_pets,policy_parking,policy_mealsForPurchase,policy_childrenMeals,policy_noShow,policy_internet,policy_additionalFees,policy_identification,policy_visaSupport
Palm Beach Resort,Resort,5,AED,Beachfront resort with pools and spa,Jumeirah Beach Road,Dubai,United Arab Emirates,00000,25.204,55.27,14:00,12:00,24 hours,+971500000000,frontdesk@palmbeach.example,https://palmbeach.example,English|Arabic,"Wellness: Spa*, Sauna; Connectivity: Free WiFi*, Parking",From 14:00,Until 12:00,Children of all ages welcome,Cribs on request,AED 500 refundable,Pets not allowed,Free valet parking,Restaurant on site,Kids eat free under 6,First night charged,Free WiFi throughout,Tourism fee AED 20/night,Passport required at check-in,Visa support letter available
City Center Hotel,Hotel,4,AED,Central business hotel near the metro,Sheikh Zayed Road,Dubai,United Arab Emirates,00000,25.211,55.279,15:00,11:00,24 hours,+971500000001,reservations@citycenter.example,https://citycenter.example,"English, French",Connectivity: Free WiFi*; Services: 24h Reception,From 15:00,Until 11:00,Children welcome,Extra bed AED 100,AED 300 refundable,No pets,Paid parking nearby,Cafe on site,Kids menu available,First night charged,Free WiFi,City tax AED 15/night,ID required,Visa letter on request
`;

const RowMenu = ({ onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - 160 });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("scroll", onScroll, true); };
  }, [open]);

  return (
    <span className="hi-kebab" ref={btnRef}>
      <button className="hi-kebab-btn" title="Options" onClick={toggle}><MoreVertical size={18} /></button>
      {open && createPortal(
        <div ref={menuRef} className="hi-kebab-menu" style={{ position: "fixed", top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
          <button className="hi-kebab-item" onClick={() => { setOpen(false); onEdit(); }}><Pencil size={14} /> Edit</button>
          <button className="hi-kebab-item danger" onClick={() => { setOpen(false); onDelete(); }}><Trash2 size={14} /> Delete</button>
        </div>,
        document.body
      )}
    </span>
  );
};

const Hotels = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");
  const [ptype, setPtype] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [sel, setSel] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rowsOptions = useMemo(() => [25, 50, 75, 100], []);

  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2600); return () => clearTimeout(t); }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status) params.set("status", status);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await adminApi("GET", `/admin/inventory/hotels${qs}`);
      setHotels(res?.data?.data?.hotels || []);
      setSel([]);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const deleteHotel = async (id) => {
    if (!window.confirm("Are you sure you want to delete this hotel?")) return;
    try {
      await adminApi("POST", `/admin/inventory/hotels/${id}/delete`);
      setToast({ type: "ok", text: "Deleted" });
      setSel((s) => s.filter((x) => x !== id));
      load();
    } catch {
      setToast({ type: "err", text: "Delete failed" });
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (!newStatus) return;
    try {
      const res = await adminApi("POST", `/admin/inventory/hotels/${id}/status`, { status: newStatus });
      if (res?.data?.statusCode !== 200 && res?.status !== 200) {
        throw new Error(res?.data?.message || "Could not change status");
      }
      if (res?.data?.statusCode >= 400) {
        throw new Error(res?.data?.message || "Could not change status");
      }
      setToast({ type: "ok", text: `Status set to ${newStatus}` });
      setHotels((prev) => prev.map((h) => (h._id === id ? { ...h, status: newStatus } : h)));
    } catch (e) {
      setToast({ type: "err", text: e?.response?.data?.message || e.message || "Could not change status" });
    }
  };
  const bulkDelete = async () => {
    if (!sel.length) return;
    if (!window.confirm(`Delete ${sel.length} hotel(s)? This also removes their rooms, rate plans, prices, availability and images.`)) return;
    try {
      for (const id of sel) {
        await adminApi("POST", `/admin/inventory/hotels/${id}/delete`);
      }
      setToast({ type: "ok", text: "Deleted" });
      load();
    } catch {
      setToast({ type: "err", text: "Some deletions failed" });
      load();
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hotels-import-sample.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${process.env.client_apiEndpoint}/admin/inventory/hotels/import`, {
        method: "POST",
        headers: { Authorization: getAdminToken() },
        body: fd,
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || "Import failed");
      const d = json?.data || {};
      setResult(d);
      setToast({ type: "ok", text: `Imported ${d.created} of ${d.total}` });
      load();
    } catch (err) {
      setToast({ type: "err", text: err.message || "Import failed" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const errorRows = (result?.results || []).filter((r) => r.status === "error");

  const cityOpts = useMemo(() => {
    const set = Array.from(new Set(hotels.map((h) => h.location?.cityName).filter(Boolean))).sort();
    return [{ value: "", label: "All cities" }, ...set.map((c) => ({ value: c, label: c }))];
  }, [hotels]);
  const typeOpts = useMemo(() => {
    const set = Array.from(new Set(hotels.map((h) => h.propertyType).filter(Boolean))).sort();
    return [{ value: "", label: "All types" }, ...set.map((t) => ({ value: t, label: t }))];
  }, [hotels]);

  const filtered = useMemo(
    () => hotels.filter((h) => (!city || h.location?.cityName === city) && (!ptype || h.propertyType === ptype)),
    [hotels, city, ptype]
  );

  useEffect(() => { setPage(1); }, [city, ptype, q, status]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * rowsPerPage;
  const pagedHotels = filtered.slice(pageStart, pageStart + rowsPerPage);

  const allSel = filtered.length > 0 && filtered.every((h) => sel.includes(h._id));
  const toggleAll = () => {
    const ids = filtered.map((h) => h._id);
    setSel((s) => (allSel ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));
  };

  return (
    <div className="gp">
      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}

      <div className="hi-head">
        <div>
          <h1 className="hi-title">Hotels <HelpTip title="How own-hotel inventory works">
            <p>Build and sell your own contracted hotels alongside the supplier hotels.</p>
            <ul>
              <li><strong>1. Create a hotel</strong> (this screen) — name, city, photos.</li>
              <li><strong>2. Add Room types</strong> for it (e.g. Deluxe King) with capacity.</li>
              <li><strong>3. Add Rate plans</strong> per room (Room Only, Breakfast…).</li>
              <li><strong>4. Set Prices</strong> (nightly rate) and open <strong>Availability</strong> (rooms to sell).</li>
              <li><strong>5. Publish</strong> the hotel — it then shows in the B2B search.</li>
            </ul>
            <p>Every row below (Rooms, Rate plans, Prices, Availability) has its own “?” for details.</p>
          </HelpTip></h1>
          <div className="hi-sub">Manage your own hotel inventory</div>
        </div>
        <div className="hi-toolbar">
          <button className="hi-btn ghost" onClick={downloadSample}>
            <Download size={16} />Sample CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onImport} />
          <button className="hi-btn soft" onClick={() => fileRef.current?.click()} disabled={importing}>
            <Upload size={16} />{importing ? "Importing…" : "Import CSV"}
          </button>
          {sel.length > 0 ? <button className="btn ghost danger" onClick={bulkDelete}>Delete Selected ({sel.length})</button> : null}
        </div>
      </div>

      {result && (
        <div className="hi-section" style={{ borderColor: errorRows.length ? "#f3d19e" : "#bfe6cd" }}>
          <div style={{ fontWeight: 700 }}>Import complete — {result.created} of {result.total} created as drafts.</div>
          {errorRows.length ? (
            <div style={{ marginTop: 8 }}>
              <div className="hi-sub" style={{ marginBottom: 4 }}>Skipped rows:</div>
              {errorRows.map((r) => (
                <div key={r.row} className="hi-sub" style={{ color: "#c33" }}>Row {r.row}: {r.message}</div>
              ))}
            </div>
          ) : null}
          <button className="btn ghost" style={{ marginTop: 10 }} onClick={() => setResult(null)}>Dismiss</button>
        </div>
      )}

      <div className="gp-card">
        <div className="hi-toolbar-row">
          <div className="hi-search">
            <Search size={18} />
            <input
              placeholder="Search hotels by name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="hi-statussel">
            <Select
              options={cityOpts}
              value={cityOpts.find((o) => o.value === city) || cityOpts[0]}
              onChange={(o) => setCity(o?.value || "")}
              components={rsComponents}
              styles={selectStyles}
              placeholder="City"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
          </div>
          <div className="hi-statussel">
            <Select
              options={typeOpts}
              value={typeOpts.find((o) => o.value === ptype) || typeOpts[0]}
              onChange={(o) => setPtype(o?.value || "")}
              components={rsComponents}
              styles={selectStyles}
              isSearchable={false}
              placeholder="Type"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
          </div>
          <div className="hi-statussel">
            <Select
              options={STATUS_OPTS}
              value={STATUS_OPTS.find((o) => o.value === status) || STATUS_OPTS[0]}
              onChange={(o) => setStatus(o?.value || "")}
              components={rsComponents}
              styles={selectStyles}
              isSearchable={false}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
          </div>
          <button className="hi-btn primary" style={{ marginLeft: "auto" }} onClick={() => navigate("/admin/hotels/new")}>
            <Plus size={18} />Hotel
          </button>
        </div>

        <div className="gp-tablewrap">
          <table className="gp-t">
            <thead>
              <tr>
                <th style={{ width: 38 }}>
                  <input type="checkbox" checked={allSel} onChange={toggleAll} title="Select all" />
                </th>
                <th style={{ width: 60 }}></th>
                <th>Name</th>
                <th style={{ width: 160 }}>City</th>
                <th style={{ width: 120 }}>Type</th>
                <th style={{ width: 80 }}>Rating</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="hi-empty">Loading...</td></tr>
              ) : hotels.length === 0 ? (
                <tr><td colSpan={8} className="hi-empty">No hotels yet. Add one, or import a CSV.</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="hi-empty">No hotels match these filters.</td></tr>
              ) : (
                pagedHotels.map((h) => (
                  <tr key={h._id} className="hi-rowlink" onClick={() => navigate("/admin/hotels/" + h._id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={sel.includes(h._id)} onChange={() => toggleSel(h._id)} />
                    </td>
                    <td>
                      {h.coverImage ? (
                        <img className="hi-thumb" src={h.coverImage} alt="" />
                      ) : (
                        <div className="hi-thumb" />
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{h.name || "-"}</td>
                    <td style={{ color: "#5b6577" }}>{h.location?.cityName || "-"}</td>
                    <td style={{ color: "#5b6577" }}>{h.propertyType || "-"}</td>
                    <td>{h.starRating ? `${h.starRating}` : "-"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Select
                        options={STATUS_OPTS.filter(o => o.value)}
                        value={STATUS_OPTS.find(o => o.value === (h.status || "draft"))}
                        onChange={(opt) => updateStatus(h._id, opt.value)}
                        styles={{
                          ...selectStyles,
                          control: (b, s) => {
                            let bg = "#fff4e5", color = "#b26a00";
                            if (h.status === "published") { bg = "#e6f7ed"; color = "#1a7f46"; }
                            if (h.status === "hidden") { bg = "#eef0f5"; color = "#6b7280"; }
                            return {
                              ...selectStyles.control(b, s),
                              minHeight: 32, height: 32, fontSize: 12, minWidth: 110, paddingLeft: 0,
                              boxShadow: "none",
                              backgroundColor: bg,
                              borderColor: "transparent",
                              borderRadius: 999
                            };
                          },
                          singleValue: (b) => {
                            let color = "#b26a00";
                            if (h.status === "published") color = "#1a7f46";
                            if (h.status === "hidden") color = "#6b7280";
                            return { ...b, color, fontWeight: 700 };
                          },
                          valueContainer: (b) => ({ ...b, padding: "0 10px" }),
                          dropdownIndicator: (b) => {
                            let color = "#b26a00";
                            if (h.status === "published") color = "#1a7f46";
                            if (h.status === "hidden") color = "#6b7280";
                            return { ...b, padding: "4px", color };
                          },
                          indicatorSeparator: () => ({ display: "none" })
                        }}
                        components={rsComponents}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ verticalAlign: "middle" }}>
                      <RowMenu onEdit={() => navigate("/admin/hotels/" + h._id)} onDelete={() => deleteHotel(h._id)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", borderTop: "1px solid #e2e6ee" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ width: 120 }}>
                <Select
                  options={rowsOptions.map(n => ({ value: String(n), label: `${n} rows` }))}
                  value={rowsPerPage ? { value: String(rowsPerPage), label: `${rowsPerPage} rows` } : null}
                  onChange={(opt) => { setRowsPerPage(Number(opt?.value) || 25); setPage(1); }}
                  components={rsComponents}
                  styles={selectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </div>
            </div>
            <div className="up-pager" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button className="up-pg-btn" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", background: "#fff", border: "1px solid #e2e6ee", borderRadius: 6, cursor: safePage <= 1 ? "not-allowed" : "pointer" }}>
                &lt; Prev
              </button>
              <span className="up-pg-info" style={{ fontSize: 13, color: "#5b6577" }}>
                Page {safePage} of {pageCount} &middot; {pageStart + 1}-{Math.min(pageStart + rowsPerPage, filtered.length)} of {filtered.length}
              </span>
              <button className="up-pg-btn" disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} style={{ padding: "6px 12px", background: "#fff", border: "1px solid #e2e6ee", borderRadius: 6, cursor: safePage >= pageCount ? "not-allowed" : "pointer" }}>
                Next &gt;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hotels;






